/**
 * Turn a photographed bank slip into what LLMR-121 needs: the image as a
 * data URL (stored so a human can review it even when nothing else worked),
 * and — best-effort — the raw text of the slip's own QR, read client-side.
 *
 * payment-gateway's /v1/slips/verify takes that raw QR text, not an image
 * (see pgw's slips.rs) — decoding happens here, in the browser, for the same
 * reason card tokenization does: it never has to become something our
 * servers parse.
 */

import jsQR from 'jsqr';

/** 6 MiB, matching member-api's maxSlipImageBytes — reject before upload
 * rather than after a round trip that was always going to 400. */
export const MAX_SLIP_IMAGE_BYTES = 6 * 1024 * 1024;

export class SlipImageError extends Error {}

/** Reads a File as a data:image/...;base64,... URL. */
export function readSlipImage(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    return Promise.reject(new SlipImageError('not_an_image'));
  }
  if (file.size > MAX_SLIP_IMAGE_BYTES) {
    return Promise.reject(new SlipImageError('too_large'));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new SlipImageError('read_failed'));
    };
    reader.onerror = () => reject(new SlipImageError('read_failed'));
    reader.readAsDataURL(file);
  });
}

function loadImage(dataURL: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new SlipImageError('decode_failed'));
    img.src = dataURL;
  });
}

/**
 * Best-effort QR read. Returns null rather than throwing when nothing is
 * found — a photo with a glare on the QR, or a bank whose slip carries no
 * QR at all, still gets uploaded; the image alone is then what a human
 * reviews, same as the design ticket's own out-of-scope note on OCR.
 */
export async function decodeSlipQr(dataURL: string): Promise<string | null> {
  const img = await loadImage(dataURL);
  const canvas = document.createElement('canvas');
  // Long side capped: jsQR's cost scales with pixel count, and a phone photo
  // easily exceeds 4000px on a side for a QR that only needs a few hundred.
  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const result = jsQR(imageData.data, imageData.width, imageData.height);
  return result?.data ?? null;
}
